<template>
  <div>
    <!-- section-introduction -->
    <NuxtLayout name="default">
      <HeroStandardApollo
        :title="$page?.faqPage.title!"
        title-size="large"
        :bg-image-source="$page?.faqPage.heroImage!"
        :full-width="false"
        class="bg-light-2"
        :gradient-opacity="0.75"
      ></HeroStandardApollo>
      <div>
        <!-- section-faq-search -->
        <section class="bg-light-2 py-space-md py-md-space-lg py-lg-space-xxl">
          <div class="container">
            <div class="row">
              <!-- filters column: visible only for desktop and above screens -->
              <div class="d-none d-lg-block col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border-bottom pb-space-xs"
                >
                  <h3 class="h3-medium">{{ filterTitle }}</h3>
                  <button
                    v-if="hasAppliedFilters"
                    @click="handleClearAll()"
                    class="fs-xs rds-link clear-btn"
                  >
                    Clear all
                  </button>
                </div>
                <!-- faq filter block -->
                <div
                  class="py-lg-space-xxs mb-space-sm border-bottom filter-categories"
                >
                  <FilterCollapseCheckboxPlus
                    component-id="faq-category-filters"
                    v-model="selectedFaqs"
                    @checked="handleFaqChecked"
                    @unchecked="handleFaqUnchecked"
                    @collapse:shown="handleFaqCollapseShown"
                    @collapse:hidden="handleFaqCollapseHidden"
                    :title="PAGE_CONSTANTS.FAQ_FILTER_TITLE"
                    :options="setFaqTypeFilterOptions"
                  />
                </div>
                <!-- Commenting as we'll need to setup a new script to handle adding a new entry to the sheet. -->
                <!-- <p>
                  Have a question we don't cover? Submit it
                  <NuxtLink @click="setModalOpen" class="rds-link">here</NuxtLink>.
                </p> -->
              </div>
              <!-- search column -->
              <div class="col-12 col-lg-9" ref="mobileFilterButton">
                <div
                  class="bg-white pt-space-xs pb-space-lg px-space-xs pt-lg-space-md pb-lg-space-xl px-lg-space-lg"
                >
                  <!-- search query row: visible only for desktop and above screens -->
                  <div id="search-query" class="row">
                    <div class="col-12 mb-0">
                      <TypeAheadSearch
                        placeholder="Search Keywords"
                        :options="filteredOptions"
                        v-model="queries"
                        @changed="handleSearchQueryChanged"
                        @debounce-changed="handleDebounched"
                        id="search-typeahead"
                      />
                    </div>
                  </div>
                  <!-- filter button & sidebar: visible only for mobile and tablet screens -->
                  <div
                    class="row bg-white pt-space-xs pt-lg-0 mb-space-sm"
                    :class="filterClasses"
                  >
                    <div class="d-block d-lg-none col-12">
                      <!-- add mobile loading -->
                      <div
                        class="overlay-loading"
                        :class="{ 'overlay-loading--show': pageLoading }"
                      >
                        <spinner-group
                          class="overlay-loader"
                          v-if="pageLoading"
                          spinner-style="border"
                        ></spinner-group>
                        <button
                          class="btn btn-dark-3 w-100 d-bold fw-bold text-white"
                          @click="handleCanvasAsideShow"
                        >
                          {{ filterTitle }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- search results row -->
                  <template v-if="pageLoading">
                    <div class="status text-center">
                      <spinner-group spinner-style="border"></spinner-group>
                    </div>
                  </template>
                  <div class="row">
                    <div
                      class="col-12 pt-space-sm pt-lg-0 mb-space-lg mb-lg-space-xl"
                      v-for="(categoryKey, index) in Object.keys(
                        searchResults
                      ).sort()"
                      :key="index"
                    >
                      <h2 class="h2-medium mb-space-sm">
                        {{ searchResults[categoryKey]["title"] }}
                      </h2>
                      <CollapseItem
                        v-for="(faq, index) in searchResults[categoryKey][
                          'items'
                        ]"
                        :key="index"
                        :collapse-id="generateFaqCollapseId(faq?.id!)"
                        :display-number="false"
                        :title="faq?.title!"
                        :highlight-width="8"
                        class="bg-white"
                        :class="{
                          'mb-space-xs': !isLastItem(
                            searchResults[categoryKey]['items'],
                            index
                          ),
                        }"
                        @collapseShown="
                          analyticsComposable.trackCollapseEvent(
                            'onclick',
                            'open',
                            'click',
                            'main content',
                            faq?.category?.title!.toLowerCase()!,
                            faq?.title?.toLowerCase()!,
                            'faq dropdown'
                          )
                        "
                        @collapseHidden="
                          analyticsComposable.trackCollapseEvent(
                            'onclick',
                            'close',
                            'click',
                            'main content',
                            faq?.category?.title?.toLowerCase()!,
                            faq?.title?.toLowerCase()!,
                            'faq dropdown'
                          )
                        "
                      >
                        <div v-html="faq.description"></div>
                      </CollapseItem>
                      <!-- Commenting as we'll need to setup a new script to handle adding a new entry to the sheet. -->
                      <!-- <p class="mb-0">
                         Have a question we don't cover? Submit it
                        <NuxtLink @click="setModalOpen" class="rds-link">here</NuxtLink>.
                      </p> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <!-- section-rfi -->
        <SectionRfiMultiStep />

        <OffCanvasAside
          v-model="isOffCanvasAsideOpen"
          direction="left"
          width="320px"
          teleportTo="body"
          bgVariant="white"
        >
          <header class="border-bottom p-space-xs">
            <div class="row">
              <div class="col-5">
                <button
                  @click="handleClearAll()"
                  :disabled="!hasAppliedFilters"
                  class="btn btn-light-2 fw-bold py-space-xxs d-block w-100"
                >
                  Clear all
                </button>
              </div>
              <div class="col-7">
                <button
                  @click="showMobileFilterResults"
                  class="btn btn-secondary fw-bold py-space-xxs d-block w-100"
                >
                  {{ sidebarApplyText }}
                </button>
              </div>
            </div>
          </header>
          <div class="sidebar-content px-space-xs overflow-y-auto">
            <!--faq filter block -->
            <div class="pb-space-xxs my-space-xs filter-categories">
              <FilterCollapseCheckboxPlus
                component-id="faq-filters-mobile"
                v-model="selectedFaqs"
                :title="PAGE_CONSTANTS.FAQ_FILTER_TITLE"
                :options="setFaqTypeFilterOptions"
                @checked="handleFaqChecked"
                @unchecked="handleFaqUnchecked"
                @collapse:shown="handleFaqCollapseShown"
                @collapse:hidden="handleFaqCollapseHidden"
              />
            </div>
          </div>
        </OffCanvasAside>
        <!-- Commenting as we'll need to setup a new script to handle adding a new entry to the sheet. -->
        <!-- <RDSModal> -->
        <!-- <RdsModal ref="awards-description-modal" use-custom-close :visible="showModal" @hidden="showModal = false"
          centered size="lg">
          <div class="d-block">
            <div class="d-flex justify-content-end align-items-center pe-space-xs pt-space-xs">
              <button type="button" aria-label="Close" class="modal-close ms-auto" @click="showModal = false">
                ×
              </button>
            </div>
            <div class="p-space-md">
              <h3 class="h3-xl mb-space-sm">
                Submit your question to ASU Online
              </h3>
              <div v-if="showAlert">{{ alertMessage }}</div>
            </div>
            <form id="faq-rfi-form" @submit.prevent="handleSubmit" data-cy="rfi-form" class="container">
              <div id="question-group" class="position-relative mb-0 p-space-sm">
                <label for="question" class="fw-bold position-relative mb-0 pb-space-md fs-large">Enter your question
                  (required field)</label>
                <textarea id="question" placeholder="Enter your question here" v-model="form.question" rows="5"
                  class="form-control rounded-0"></textarea>
              </div>

              <div id="email-group" class="position-relative mb-0 p-space-sm">
                <label for="email" class="fw-bold position-relative mb-0 pb-space-md fs-large">Enter your email to
                  receive a response (required
                  field)</label>
                <input id="email" type="email" placeholder="Enter your email address" v-model="form.email"
                  @input="handleEmailInput()" @blur="handleEmailBlur()" class="form-control rounded-0" :class="{
                    'is-invalid': emailErrorMessage,
                  }" data-cy="rfi-email" />
                <div v-if="emailErrorMessage" class="invalid-feedback">
                  {{ emailErrorMessage }}
                </div>
              </div>
              <div class="position-relative mb-0 p-space-sm">
                <div class="col-12">
                  <div class="d-flex align-items-center justify-content-end">
                    <button class="fw-bold py-space-xxs px-space-md btn btn-secondary" type="submit" variant="secondary"
                      data-cy="faq-submit-button" :disabled="isSubmitDisabled">
                      Submit
                      <div class="d-inline" v-if="showLoader">
                        <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                        <span class="sr-only">Loading...</span>
                        <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                        <span class="sr-only">Loading...</span>
                        <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                        <span class="sr-only">Loading...</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </RdsModal> -->
      </div>
    </NuxtLayout>
  </div>
</template>
<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { debouncedRef, useWindowSize } from "@vueuse/core";
import { UseFuseOptions, useFuse } from "@vueuse/integrations/useFuse";
import { FuseResult } from "fuse.js";
import OffCanvasAside from "~/components/OffCanvasAside.vue";
import SpinnerGroup from "~/components/SpinnerGroup.vue";
import TypeAheadSearch, {
  type TypeAheadSearchListOption,
} from "~/components/TypeAheadSearch.vue";
import FilterCollapseCheckboxPlus, {
  FilterCollapseOption,
} from "~/components/degree-search/FilterCollapseCheckboxPlus.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import { Faq, FaqCategory, FaqPage } from "~/server/resolvers/resolvers-types";

interface faqPageResponse {
  faqPage: FaqPage;
  allFaqCategory: FaqCategory[];
  allFaq: Faq[];
}
/*
 ********************************************
 * Page query
 ********************************************
 */
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});
const $page = await usePageQuery<faqPageResponse>(
  `#graphql
      query faqPage($faqPageId: ID!){
        faqPage(id:$faqPageId) {
          id
          detailPage
          metaTitle
          metaDescription
          heroImage
          title
        }
        allFaqCategory {
          id
          slug
          title
        }
        allFaq {
          id
          title
          description
          category {
            slug
            title
          }
        }
      }
      `,
  {
    faqPageId: "e84a5b79-93ea-4a64-9e72-3ad724d76de5",
  },
  {
    key: "faq-page",
  }
);

/*
 ********************************************
 * Page metadata
 ********************************************
 */
useHead({
  title: $page?.faqPage.metaTitle,
  meta: [
    {
      name: "description",
      content: $page?.faqPage.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: $page?.faqPage.detailPage as string,
    },
  ],
  script: [
    useSchemaService().generateAboutUsFaqSchema($page?.faqPage, $page?.allFaq),
  ],
});

/*
 ********************************************
 * Enums
 ********************************************
 */
enum PAGE_CONSTANTS {
  FAQ_FILTER_TITLE = "FAQ category",
  UPDATE_TEXT = "Update",
  CLOSE_TEXT = "Close",
  FILTERS_TEXT = "Filters",
  SECTION_TITLE = "FAQ",
}
/*
 ********************************************
 * Data
 ********************************************
 */
const selectedFaqs = ref<Set<string>>(new Set());
const isOffCanvasAsideOpen = ref<boolean>(false);
const pageLoading = ref<boolean>(true);
const UtilityService = useUtilityService();
const { width: windowWidth } = useWindowSize();
/*
 ********************************************
 * Computed
 ********************************************
 */

const filteredOptions = computed<TypeAheadSearchListOption[]>(() => {
  return (
    resultsFiltered.value.map(({ item }) => {
      return {
        value: item?.title!,
        text: item?.title!,
      }!;
    }) || []
  );
});

const searchResults = computed(() => {
  const result = {} as { [key: string]: { title: string; items: Faq[] } };
  resultsFiltered.value.forEach((faq) => {
    if (faq.item && faq.item.category && faq.item.category.slug) {
      const categorySlug = faq.item.category.slug;
      if (!result[categorySlug]) {
        result[categorySlug] = {
          title: faq?.item?.category?.title!,
          items: [],
        };
      }
      result[categorySlug].items.push(faq.item);
    }
  });
  return result;
});

const isLastItem = (collection: Faq[], currentIndex: number) => {
  return currentIndex === collection.length - 1;
};

const generateFaqCollapseId = (id: string) => {
  return `faq-${id}`;
};

const sidebarApplyText = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.UPDATE_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.CLOSE_TEXT;
});

const totalAppliedFilters = computed<number>(() => {
  return selectedFaqs.value.size;
});
const hasAppliedFilters = computed<boolean>(() => {
  return totalAppliedFilters.value > 0;
});

const filterClasses = computed(() => {
  return isDesktopScreen.value === false
    ? ` filter-div sticky-top pb-space-sm border-bottom `
    : null;
});

const filterTitle = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.FILTERS_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.FILTERS_TEXT;
});

const setFaqTypeFilterOptions = computed(() => {
  const filterCategory: FaqCategory[] = $page?.allFaqCategory!;
  const options: FilterCollapseOption[] = [];

  const categoryOption = (category: FaqCategory) => {
    options.push({
      text: category.title!,
      value: category.slug!,
      description: category.slug!,
    });
  };

  if (Array.isArray(filterCategory)) {
    filterCategory.forEach((category) => {
      categoryOption(category);
    });
  } else {
    categoryOption(filterCategory);
  }

  options.sort(UtilityService.sortByText);
  return options;
});

/*
 ********************************************
 * Methods
 ********************************************
 */
const handleClearAll = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "filters",
    "clear all",
    "filters"
  );
  selectedFaqs.value.clear();
};

const handleCanvasAsideShow = () => {
  isOffCanvasAsideOpen.value = true;
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    "frequently asked questions",
    "filters",
    "filters"
  );
};

const mobileFilterButton = ref<HTMLElement | null>(null);
const showMobileFilterResults = () => {
  // scroll to searchbar
  mobileFilterButton.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "filters",
    hasAppliedFilters.value
      ? PAGE_CONSTANTS.UPDATE_TEXT.toLowerCase()
      : PAGE_CONSTANTS.CLOSE_TEXT.toLowerCase(),
    "filters"
  );
  isOffCanvasAsideOpen.value = false;
};
/*
 ********************************************
 * Analytics
 ********************************************
 */

const handleFaqChecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "faq category",
    option.text.toLowerCase(),
    "filters"
  );
};
const handleFaqUnchecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "faq category",
    option.text.toLowerCase(),
    "filters"
  );
};

const handleFaqCollapseShown = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "open",
    "click",
    "main content",
    "filters",
    "faq category",
    "frequently asked questions"
  );
};

const handleFaqCollapseHidden = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "close",
    "click",
    "main content",
    "filters",
    "faq category",
    "frequently asked questions"
  );
};

interface SearchOption {
  value: string;
  text: string;
}
const handleSearchQueryChanged = (searchOption: SearchOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "search keywords",
    "main content",
    "frequently asked questions",
    searchOption.text.toLowerCase(),
    "search bar"
  );
};

/*
 ********************************************
 * Reactive
 ********************************************
 */
const isDesktopScreen = computed(() => {
  return windowWidth.value > 992;
});

/*
 ********************************************
 * Fuse search
 ********************************************
 */

const fuseOptions: UseFuseOptions<Faq> = {
  fuseOptions: {
    // set minMatchCharLength to 1 otherwise search bar will not filter programs with long title
    isCaseSensitive: false,
    includeScore: true,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.6,
    distance: 100,
    keys: ["title", "category.slug"],
  },
  matchAllWhenSearchEmpty: true,
};
const queries = ref<string | null>(null);
const searchOptions = ref<Faq[]>($page?.allFaq ?? []);
const resultsFiltered = ref<FuseResult<Faq>[]>([]);
// matchAllWhenSearchEmpty: true, to show all programs when search bar is empty
const { fuse, results } = useFuse("", searchOptions, fuseOptions);

const debouncedSearchQuery = debouncedRef(queries, 350);

const preparedSchema = computed(() => {
  const schema = [];
  if (selectedFaqs.value.size > 0) {
    schema.push({
      $or: Array.from(selectedFaqs.value).map((type) => {
        return { "category.slug": `=${type}` };
      }),
    });
  }
  if (debouncedSearchQuery.value && debouncedSearchQuery.value.length > 0) {
    schema.push({ title: debouncedSearchQuery.value });
  }
  return schema;
});

watchEffect(() => {
  if (preparedSchema.value.length > 0) {
    const results = fuse.value.search({
      $and: preparedSchema.value,
    });
    resultsFiltered.value = results;
  } else {
    resultsFiltered.value = results.value;
  }
});

const handleDebounched = (text: string | null) => {
  analyticsComposable.trackEvent({
    event: "search",
    action: "type",
    name: "onenter",
    type: "search keywords",
    region: "main content",
    section: "frequently asked questions",
    text: text ? text.toLowerCase() : "",
  });
};

onMounted(async () => {
  await nextTick();
  pageLoading.value = false;
});

// Commenting as we'll need to setup a new script to handle adding a new entry to the sheet.
// interface FaqFormFieldsRef {
//   email?: string;
//   question?: string;
// }

// const alertMessage = ref("");
// const showModal = ref<boolean>(false);
// const isEmailValid = ref<boolean | null>(null);
// const formDisabled = ref(false);

// const form = ref<FaqFormFieldsRef>({
//   email: "",
//   question: ""
// });

// const setModalOpen = () => {
//   showModal.value = true;
//   analyticsComposable.trackModalEvent(
//     "onclick",
//     "open",
//     "click",
//     "main content",
//     $page?.faqPage.title!.toLowerCase()!,
//     "here"
//   );
// };

// const handleEmailBlur = () => {
//   const text = form.value.email ? form.value.email.toLowerCase() : "";
//   analyticsComposable.trackFormEvent(
//     "onclick",
//     "click",
//     "blur",
//     "main content",
//     "request info ^ email",
//     text
//   );
// };

// const isSubmitDisabled = computed<boolean>(() => {
//   return (
//     form.value.email === undefined ||
//     form.value.email === "" ||
//     form.value.question === undefined ||
//     form.value.question === "" ||
//     showLoader.value === true
//   );
// });

// const handleSubmit = () => {
//   showLoader.value = true;
//   const formTrackingData = {
//     question: form.value.question ? form.value.question.toLowerCase() : null,
//     email: form.value.email ? form.value.email.toLowerCase() : null
//   };

//   axios.get(
//     "https://script.google.com/macros/s/AKfycbzSuPKVBX770vRnn95zluxN3YuKaRxehPyOJO2dAVmmm7YXLSkBw-a4ikN9BuLgVN_Mvg/exec",
//     {
//       headers: {
//         "Content-Type": "application/json",
//       },
//       params: {
//         question: form.value.question,
//         email: form.value.email,
//         timestamp: new Date().toUTCString(),
//         origin_url: window.location.origin
//       }
//     })
//     .then((res) => {
//       showLoader.value = false;
//       alertMessage.value = "Question successfully submitted.";
//     })
//     .catch((error) => {
//       console.error(error);
//       alertMessage.value = "Something went wrong. Please try again later.";
//     })
//     .then(() => {
//       showAlert.value = true;
//       showLoader.value = false;
//     });
// };

// const handleEmailInput = () => {
//   isEmailValid.value = null;
//   formDisabled.value = false;
// };

// const emailErrorMessage = computed(() => {
//   if (form.value.hasOwnProperty("email") && form.value.email !== "") {
//     let result = faqZodSchema.shape.email.safeParse(form.value.email);
//     if (!result.success) {
//       // Passes back the most recent error, potential to list all validation errors
//       return result.error.errors[0].message;
//     } else if (isEmailValid.value === false) {
//       return "Please enter a valid email address and try again.";
//     }
//   }
//   return "";
// });
</script>

<style lang="scss" scoped>
.filter-div {
  z-index: 1;
}

.status {
  min-height: 700px;
}

.clear-btn {
  color: var(--rds-primary, #8c1d40);
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.overlay-loading {
  &--show {
    position: relative;
    display: block;

    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--rds-dark-3);
      opacity: 0.8;
      z-index: 3;
    }

    .overlay-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 4;
    }
  }
}
:deep(main#content) {
  background-color: var(--rds-light-2);
}
</style>
<style style="scss">
.filter-categories {
  .form-check-input:checked {
    background-color: var(--rds-secondary);
    border-color: var(--rds-secondary);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%23191919' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3e%3c/svg%3e");
    background-size: 66%;
  }
}
</style>
